import { useState } from "react";
import PiMegaphoneAnnouncementShoutStroke from "@markeroo/icons/src/stroke/general/PiMegaphoneAnnouncementShoutStroke";
import PiInformationCircleStroke from "@markeroo/icons/src/stroke/alerts/PiInformationCircleStroke";
import PiMultipleCrossCancelDefaultStroke from "@markeroo/icons/src/stroke/maths/PiMultipleCrossCancelDefaultStroke";
import PiAlertTriangleStroke from "@markeroo/icons/src/stroke/alerts/PiAlertTriangleStroke";
import { ui } from "@/strings";

export type InfoBannerVariant = "info" | "warning";

export interface InfoBannerProps {
  /** The main announcement title text */
  title: string;
  /** List of info bullet points to display below the title */
  infoLines?: string[];
  /** Visual variant - changes background color and default icon */
  variant?: InfoBannerVariant;
  /** Whether the banner can be dismissed (default: true) */
  dismissible?: boolean;
  /** Callback when the banner is dismissed */
  onDismiss?: () => void;
  /** Optional action element (button/link) - placed in bottom right */
  action?: React.ReactNode;
  /** Additional CSS classes for the container */
  className?: string;
}

const variantStyles: Record<InfoBannerVariant, {
  container: string;
  icon: string;
  text: string;
  DefaultIcon: React.ComponentType<{ size: number; className: string }>;
}> = {
  info: {
    container: "bg-brand-50 border-brand-200",
    icon: "text-brand-700",
    text: "text-text-theme-primary",
    DefaultIcon: PiMegaphoneAnnouncementShoutStroke,
  },
  warning: {
    container: "bg-semantic-warning-bg border-semantic-warning-border",
    icon: "text-semantic-warning",
    text: "text-semantic-warning-text",
    DefaultIcon: PiAlertTriangleStroke,
  },
};

/**
 * An info banner component for announcements and contextual information.
 *
 * Consistent design:
 * - 40px icon + semibold title
 * - Optional info lines with 22px info-circle icons, indented
 * - Optional action button in bottom right (for CTAs like "Learn More" or "Upgrade")
 * - Dismissible X button in top right (optional)
 *
 * Only the color varies by variant.
 */
export function InfoBanner({
  title,
  infoLines = [],
  variant = "info",
  dismissible = true,
  onDismiss,
  action,
  className = "",
}: InfoBannerProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  if (!isVisible) return null;

  const styles = variantStyles[variant];
  const IconComponent = styles.DefaultIcon;

  return (
    <div className={`${styles.container} border rounded-xl pt-5 pb-6 px-6 ${className}`}>
      <div className="flex flex-col gap-4">
        {/* Header row: icon + title + dismiss button */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3">
            <IconComponent size={40} className={`${styles.icon} flex-shrink-0`} />
            <p className={`font-semibold text-label-xl leading-5 ${styles.text}`}>
              {title}
            </p>
          </div>

          {/* Dismiss button */}
          {dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              className="p-1 text-text-theme-muted hover:text-text-theme-secondary rounded focus:outline-none focus:ring-2 focus:ring-brand-600"
              aria-label={ui.actions.close}
            >
              <PiMultipleCrossCancelDefaultStroke size={20} />
            </button>
          )}
        </div>

        {/* Info lines */}
        {infoLines.length > 0 && (
          <div className="flex flex-col gap-2 pl-5">
            {infoLines.map((line, index) => (
              <div key={index} className="flex items-start gap-2">
                <PiInformationCircleStroke size={22} className={`${styles.text} flex-shrink-0 mt-0.5`} />
                <p className={`text-body ${styles.text}`}>
                  {line}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Action button - bottom right */}
        {action && (
          <div className="flex justify-end">
            {action}
          </div>
        )}
      </div>
    </div>
  );
}
